import { derived, writable } from "svelte/store";
import type { PrivateWriteUser } from "@outverse-video/outverse-api-client-lib/src/api";
import type { Content } from "@tiptap/core";
import type { Author } from "app/models/forums";
import type { Content as EditorContent } from "@tiptap/core";

export interface ReplyComposer {
  threadUuid: string;
  uuid: string;
  author?: PrivateWriteUser | null;
  initialContent?: EditorContent;
  view: ReplyView;
  reply: DraftThreadPost;
  hasDraft: boolean;
}

export type DraftThreadPost = {
  content: Content;
  text: string;
  replyToPostUuid: string;
  respondee?: Author;
  uuid: string;
};

export type ReplyView = "minimised" | "editing" | "fullscreen";

const initialState: ReplyComposer = {
  threadUuid: "",
  uuid: "",
  author: null,
  initialContent: "",
  view: "minimised",
  hasDraft: false,
  reply: {
    content: "",
    text: "",
    replyToPostUuid: "",
    respondee: undefined,
    uuid: "",
  },
};

const replyComposerStore = () => {
  const { subscribe, set, update } = writable<ReplyComposer>(initialState);

  return {
    subscribe,
    update,
    set,
    replyToThread: (threadUuid: string) => {
      update((state) => {
        if (state.reply.uuid) {
          state.initialContent = "";
        }
        // clear any 'reply to post' content
        const reply = {
          ...state.reply,
          replyToPostUuid: "",
          respondee: undefined,
          uuid: "",
        };
        state = { ...state, threadUuid, reply, view: "editing" };
        return state;
      });
    },
    replyToPost: (threadUuid: string, replyToPostUuid: string, respondee: Author) => {
      update((state) => {
        if (state.reply.uuid) {
          state.initialContent = "";
        }
        const reply = { ...state.reply, replyToPostUuid, respondee, uuid: "" };
        state = { ...state, threadUuid, reply, view: "editing" };
        return state;
      });
    },
    editPost: (threadUuid: string, replyUuid: string, initialContent: Content) => {
      update((state) => {
        const reply = { ...state.reply, uuid: replyUuid };
        state = {
          ...state,
          initialContent,
          threadUuid,
          reply,
          view: "editing",
        };
        return state;
      });
    },
    setView: (view: ReplyView) => {
      update((state) => {
        state.view = view;
        return state;
      });
    },
    setDraft: (replyDraft: Partial<DraftThreadPost>, empty: boolean) => {
      update((state) => {
        state.reply = { ...state.reply, ...replyDraft };
        state.hasDraft = !empty;
        return state;
      });
    },
    clear: () => {
      update(() => {
        return {
          threadUuid: "",
          uuid: "",
          author: null,
          initialContent: "",
          view: "minimised",
          hasDraft: false,
          reply: {
            content: "",
            text: "",
            replyToPostUuid: "",
            respondee: undefined,
            uuid: "",
          },
        };
      });
    },
  };
};

export const replyComposer = replyComposerStore();

export const replyDraft = derived(replyComposer, ($replyComposer) => $replyComposer.reply);
